/* ==========================================================================
   KG custom scripts
   ========================================================================== */

/* --------------------------------------------------------------------------
   KG slider (sections/kg-reviews.liquid)
   Markup hooks: [data-kg-slider] > [data-kg-slider-track] > [data-kg-slider-item],
   plus [data-kg-slider-prev] / [data-kg-slider-next] buttons.
   The track scrolls natively (scroll-snap in CSS); the buttons scroll it by one
   card and are disabled at the start / end of the track.
   -------------------------------------------------------------------------- */

(function () {
  'use strict';

  var EDGE_TOLERANCE = 2;

  function initSlider(root) {
    if (!root || root.hasAttribute('data-kg-slider-ready')) return;

    var track = root.querySelector('[data-kg-slider-track]');
    var prev = root.querySelector('[data-kg-slider-prev]');
    var next = root.querySelector('[data-kg-slider-next]');
    if (!track || !prev || !next) return;

    root.setAttribute('data-kg-slider-ready', '');

    function getStep() {
      var item = track.querySelector('[data-kg-slider-item]');
      if (!item) return track.clientWidth;
      var gap = parseFloat(window.getComputedStyle(track).columnGap) || 0;
      return item.getBoundingClientRect().width + gap;
    }

    function update() {
      var maxScroll = track.scrollWidth - track.clientWidth;
      prev.disabled = track.scrollLeft <= EDGE_TOLERANCE;
      next.disabled = track.scrollLeft >= maxScroll - EDGE_TOLERANCE;
    }

    function scrollByStep(direction) {
      var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      track.scrollBy({
        left: direction * getStep(),
        behavior: reduceMotion ? 'auto' : 'smooth',
      });
    }

    prev.addEventListener('click', function () {
      scrollByStep(-1);
    });

    next.addEventListener('click', function () {
      scrollByStep(1);
    });

    track.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);

    update();
  }

  function initAll(container) {
    (container || document).querySelectorAll('[data-kg-slider]').forEach(initSlider);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  /* Theme editor: re-run when a section is added or re-rendered */
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();

/* --------------------------------------------------------------------------
   KG accordion (sections/kg-faq.liquid)
   Items are native <details>/<summary>. A click on the summary slides the item
   open / closed by animating its height (Web Animations API). When the list
   has [data-kg-accordion], opening one item closes the others.
   One delegated listener, so it also covers sections added later in the editor.
   -------------------------------------------------------------------------- */

(function () {
  'use strict';

  var DURATION = 300;
  var EASING = 'ease-out';

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function finish(details, shouldBeOpen) {
    details.open = shouldBeOpen;
    details.removeAttribute('data-kg-closing');
    details.style.height = '';
    details.style.overflow = '';
    details.kgAnimation = null;
  }

  function slide(details, shouldOpen) {
    if (details.kgAnimation) details.kgAnimation.cancel();

    var summary = details.querySelector('summary');
    var style = window.getComputedStyle(details);
    var startHeight = details.offsetHeight;
    var endHeight;

    details.style.overflow = 'hidden';

    if (shouldOpen) {
      details.open = true;
      details.removeAttribute('data-kg-closing');
      endHeight = details.offsetHeight;
    } else {
      details.setAttribute('data-kg-closing', '');
      endHeight =
        summary.offsetHeight + parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);
    }

    var animation = details.animate(
      { height: [startHeight + 'px', endHeight + 'px'] },
      { duration: DURATION, easing: EASING }
    );

    details.kgAnimation = animation;
    animation.onfinish = function () {
      finish(details, shouldOpen);
    };
    animation.oncancel = function () {
      if (details.kgAnimation === animation) {
        details.style.height = '';
        details.style.overflow = '';
      }
    };
  }

  function isOpenOrOpening(details) {
    return details.open && !details.hasAttribute('data-kg-closing');
  }

  document.addEventListener('click', function (event) {
    var summary = event.target.closest && event.target.closest('.kg-faq__summary');
    if (!summary) return;

    var details = summary.parentElement;
    if (!details || details.tagName !== 'DETAILS') return;

    /* Without animation support or with reduced motion, keep the native behavior */
    if (prefersReducedMotion() || typeof details.animate !== 'function') {
      var list = details.closest('[data-kg-accordion]');
      if (list && !details.open) {
        list.querySelectorAll('details[open]').forEach(function (other) {
          if (other !== details) other.open = false;
        });
      }
      return;
    }

    event.preventDefault();

    var shouldOpen = !isOpenOrOpening(details);

    if (shouldOpen) {
      var accordion = details.closest('[data-kg-accordion]');
      if (accordion) {
        accordion.querySelectorAll('details').forEach(function (other) {
          if (other !== details && isOpenOrOpening(other)) slide(other, false);
        });
      }
    }

    slide(details, shouldOpen);
  });
})();

/* --------------------------------------------------------------------------
   KG product view (snippets/kg-product-view.liquid)
   Used by sections/kg-choose-gift.liquid and sections/kg-main-product.liquid.
   - [data-kg-radio-group] > [data-kg-radio]: package cards. A card with
     data-target shows that product panel; a card with data-variant-id switches
     the variant of the product on the page.
   - [data-kg-product]: gallery thumbs, quantity, live total, Buy now.
   -------------------------------------------------------------------------- */

(function () {
  'use strict';

  function formatMoney(cents) {
    var currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';
    var locale = document.documentElement.lang || undefined;
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency: currency }).format(cents / 100);
    } catch (error) {
      return (cents / 100).toFixed(2);
    }
  }

  function initProduct(root) {
    if (!root || root.hasAttribute('data-kg-product-ready')) return;
    root.setAttribute('data-kg-product-ready', '');

    var form = root.querySelector('form');
    var qtyInput = root.querySelector('[data-kg-qty]');
    var decButton = root.querySelector('[data-kg-qty-dec]');
    var incButton = root.querySelector('[data-kg-qty-inc]');
    var total = root.querySelector('[data-kg-total]');
    var price = root.querySelector('[data-kg-price]');
    var mainImage = root.querySelector('[data-kg-main-image]');
    var thumbs = root.querySelectorAll('[data-kg-thumb]');
    var variantInput = root.querySelector('[data-kg-variant-id]');
    var returnTo = root.querySelector('[data-kg-return-to]');
    var addButton = root.querySelector('[data-kg-add]');
    var addLabel = root.querySelector('[data-kg-add-label]');
    var buyNow = root.querySelector('[data-kg-buy-now]');
    var labelAdd = root.getAttribute('data-kg-label-add');
    var labelSoldOut = root.getAttribute('data-kg-label-soldout');

    function getQty() {
      var value = parseInt(qtyInput && qtyInput.value, 10);
      if (isNaN(value) || value < 1) value = 1;
      if (value > 99) value = 99;
      return value;
    }

    function updateTotal() {
      var qty = getQty();
      if (qtyInput) qtyInput.value = qty;
      if (decButton) decButton.disabled = qty <= 1;
      if (total) total.textContent = formatMoney(Number(total.getAttribute('data-kg-unit-price')) * qty);
    }

    if (qtyInput) {
      qtyInput.addEventListener('input', updateTotal);
      qtyInput.addEventListener('change', updateTotal);
    }
    if (decButton) {
      decButton.addEventListener('click', function () {
        qtyInput.value = getQty() - 1;
        updateTotal();
      });
    }
    if (incButton) {
      incButton.addEventListener('click', function () {
        qtyInput.value = getQty() + 1;
        updateTotal();
      });
    }

    function showThumb(thumb) {
      if (!mainImage || !thumb) return;
      mainImage.src = thumb.getAttribute('data-src');
      mainImage.srcset = thumb.getAttribute('data-srcset');
      mainImage.alt = thumb.getAttribute('data-alt') || '';
      thumbs.forEach(function (item) {
        var active = item === thumb;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    }

    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        showThumb(thumb);
      });
    });

    /* Buy now: add to cart and go straight to the checkout (plain form post) */
    if (buyNow && form) {
      buyNow.addEventListener('click', function () {
        if (variantInput && variantInput.disabled) return;
        if (returnTo) returnTo.disabled = false;
        form.submit();
      });
    }

    /* Called by the package cards when the product has several variants */
    root.kgSetVariant = function (variant) {
      if (!variant) return;

      if (variantInput) {
        variantInput.value = variant.id;
        variantInput.disabled = !variant.available;
      }
      if (price) price.textContent = variant.priceFormatted;
      if (total) total.setAttribute('data-kg-unit-price', variant.price);
      if (addButton) addButton.disabled = !variant.available;
      if (buyNow) buyNow.disabled = !variant.available;
      if (addLabel) addLabel.textContent = variant.available ? labelAdd : labelSoldOut;
      updateTotal();

      if (variant.mediaId) {
        thumbs.forEach(function (thumb) {
          if (thumb.getAttribute('data-media-id') === String(variant.mediaId)) showThumb(thumb);
        });
      }

      if (window.history && window.history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    };

    updateTotal();
  }

  function initRadioGroup(group) {
    if (!group || group.hasAttribute('data-kg-radio-ready')) return;
    group.setAttribute('data-kg-radio-ready', '');

    var gift = group.closest('[data-kg-gift]');
    var items = Array.prototype.slice.call(group.querySelectorAll('[data-kg-radio]'));

    function select(item, moveFocus) {
      items.forEach(function (other) {
        var active = other === item;
        other.setAttribute('aria-checked', active ? 'true' : 'false');
        other.setAttribute('tabindex', active ? '0' : '-1');
        other.classList.toggle('is-selected', active);
      });
      if (moveFocus) item.focus();
      if (!gift) return;

      var target = item.getAttribute('data-target');
      if (target) {
        gift.querySelectorAll('[data-kg-panel]').forEach(function (panel) {
          panel.hidden = panel.id !== target;
        });
      }

      var variantId = item.getAttribute('data-variant-id');
      if (variantId) {
        var product = gift.querySelector('[data-kg-product]');
        var data = product && product.querySelector('script[data-kg-variants]');
        if (!product || !data || !product.kgSetVariant) return;
        try {
          var variants = JSON.parse(data.textContent);
          product.kgSetVariant(
            variants.filter(function (variant) {
              return String(variant.id) === variantId;
            })[0]
          );
        } catch (error) {
          /* invalid variant data: keep the current variant */
        }
      }
    }

    items.forEach(function (item, index) {
      item.addEventListener('click', function () {
        select(item, false);
      });
      item.addEventListener('keydown', function (event) {
        var key = event.key;
        if (key === ' ' || key === 'Enter') {
          event.preventDefault();
          select(item, false);
        } else if (key === 'ArrowRight' || key === 'ArrowDown') {
          event.preventDefault();
          select(items[(index + 1) % items.length], true);
        } else if (key === 'ArrowLeft' || key === 'ArrowUp') {
          event.preventDefault();
          select(items[(index - 1 + items.length) % items.length], true);
        }
      });
    });
  }

  function initAll(container) {
    var scope = container || document;
    scope.querySelectorAll('[data-kg-product]').forEach(initProduct);
    scope.querySelectorAll('[data-kg-radio-group]').forEach(initRadioGroup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();

/* --------------------------------------------------------------------------
   Dein Island Contact form (sections/dein-island-contact.liquid)
   Message counter and client-side validation before the form is submitted.
   -------------------------------------------------------------------------- */

(function () {
  'use strict';

  function initContact(root) {
    if (!root || root.hasAttribute('data-di-contact-ready')) return;

    var form = root.querySelector('.dein-island-contact__form');
    if (!form) return;

    var msg = form.querySelector('[data-message]');
    var count = form.querySelector('[data-count]');
    var consent = form.querySelector('[data-consent]');
    var consentError = form.querySelector('[data-consent-error]');
    if (!msg || !count || !consent || !consentError) return;

    root.setAttribute('data-di-contact-ready', '');

    function onCount() {
      count.textContent = msg.value.length + ' / 1000';
    }
    msg.addEventListener('input', onCount);
    onCount();

    function setError(input, bad) {
      var field = input.closest('[data-field]');
      if (field) field.toggleAttribute('data-invalid', bad);
      input.setAttribute('aria-invalid', bad ? 'true' : 'false');
    }

    form.addEventListener('submit', function (event) {
      var name = form.querySelector('#ContactForm-name');
      var email = form.querySelector('#ContactForm-email');
      var checks = [
        [name, !name.value.trim()],
        [email, !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value.trim())],
        [msg, msg.value.trim().length < 10],
      ];
      var first = null;

      checks.forEach(function (check) {
        setError(check[0], check[1]);
        if (check[1] && !first) first = check[0];
      });

      var noConsent = !consent.checked;
      consentError.hidden = !noConsent;
      if (noConsent && !first) first = consent;

      if (first) {
        event.preventDefault();
        first.focus();
      }
    });
  }

  function initAll(container) {
    (container || document).querySelectorAll('.dein-island-contact').forEach(initContact);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
